import extractQueryParamFromPageLocation from '@src/quince-site-common/src/utils/extractQueryParamFromPageLocation'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { getCookieStorage } from '@src/utils/enhancedCookie'

/**
 * Snapchat tracking constants
 * Based on Snapchat CAPI documentation
 */
export const SNAPCHAT_TRACKING_CONSTANTS = {
  // Snapchat adds ScCid to destination URL, then sets cookies
  CLICK_ID_URL_PARAM: 'ScCid',
  CLICK_ID_COOKIE: '_quince_scid', // Our own cookie, separate from Snapchat pixel's _scid
  // Snapchat recommends 7-day expiration for scid cookie
  COOKIE_EXPIRY_MINUTES: 7 * 24 * 60,
} as const

/**
 * Extracts query parameter from referrer URL
 */
const extractQueryParamFromReferrer = (key: string): string => {
  try {
    if (typeof window === 'undefined' || !window.document.referrer) {
      return ''
    }

    const url = new URL(window.document.referrer)
    return url.searchParams.get(key) ?? ''
  } catch (error) {
    return ''
  }
}

/**
 * Sets the _quince_scid cookie when ScCid URL parameter is present
 */
const setSnapchatClickIdCookie = (scid: string): void => {
  if (scid && typeof window !== 'undefined') {
    const cookieStorage = getCookieStorage()
    cookieStorage.setCookieLocalized(SNAPCHAT_TRACKING_CONSTANTS.CLICK_ID_COOKIE, scid, {
      isGlobal: true,
      expires: new Date(Date.now() + SNAPCHAT_TRACKING_CONSTANTS.COOKIE_EXPIRY_MINUTES * 60 * 1000),
      secure: true,
    })
  }
}

/**
 * Extracts Snapchat Click ID (ScCid) with proper priority
 * Priority: URL Parameter > Referrer > Cookie
 *
 * Snapchat flow: URL parameter → Cookie → Our extraction
 * We use cookie fallback to maintain attribution on subsequent pages after the initial click
 */
export const getSnapchatClickId = (): string => {
  // 1. First try URL parameter (Snapchat adds ScCid to destination URL)
  const urlClickId: string = extractQueryParamFromPageLocation(SNAPCHAT_TRACKING_CONSTANTS.CLICK_ID_URL_PARAM)
  if (urlClickId) {
    // Set the cookie when we find the URL parameter
    setSnapchatClickIdCookie(urlClickId)
    return urlClickId
  }

  // 2. Fallback to referrer (if available)
  const referrerClickId: string = extractQueryParamFromReferrer(SNAPCHAT_TRACKING_CONSTANTS.CLICK_ID_URL_PARAM)
  if (referrerClickId) {
    // Set the cookie when we find the referrer parameter
    setSnapchatClickIdCookie(referrerClickId)
    return referrerClickId
  }

  // 3. Fallback to stored cookie (for subsequent events in the same session)
  // This is critical for maintaining attribution when users navigate to other pages
  const cookieClickId = getCookie(SNAPCHAT_TRACKING_CONSTANTS.CLICK_ID_COOKIE)
  if (cookieClickId) {
    return cookieClickId
  }

  return ''
}

/**
 * Extracts Snapchat Cookie ID from our own cookie
 * This is separate from Snapchat's _scid cookie to avoid conflicts
 */
export const getSnapchatCookieId = (): string => {
  const scid = getCookie(SNAPCHAT_TRACKING_CONSTANTS.CLICK_ID_COOKIE)
  return scid ?? ''
}

/**
 * Gets all Snapchat tracking data as an object
 */
export const getSnapchatTrackingData = (): {
  sc_click_id: string
  sc_cookie1: string
} => {
  // sc_click_id is the click ID from URL, referrer, or cookie (prioritizing URL)
  const clickId = getSnapchatClickId()

  // sc_cookie1 is our own cookie value (_quince_scid)
  const cookieId = getSnapchatCookieId()

  return {
    sc_click_id: clickId,
    sc_cookie1: cookieId,
  }
}
